// src/components/CommonComponents/ProtectedRoute.js
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { authStorage } from "../utils/authStorage";

const ProtectedRoute = ({ children, allowedRoles = [], allowedRoleNames = [], disallowManager = false }) => {
    const location = useLocation();
    const { isAuthenticated, hasRole, logout, isRehydrated } = useAuth();

    // Wait for rehydration before checking auth
    if (!isRehydrated) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-4 text-lg">Loading...</span>
            </div>
        );
    }

    // Not logged in → redirect to login
    if (!isAuthenticated()) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    const loginContext = authStorage.getLoginContext();
    const contextMismatch = loginContext === "admin" && allowedRoles.includes(2)
        ? true
        : loginContext === "pos" && allowedRoles.includes(1)
            ? true
            : false;

    const user = authStorage.getUser();
    const isManager = user?.user_type === 2
        && String(user?.role_name || "").toLowerCase() === "manager";
    const roleName = String(user?.role_name || "").toLowerCase();
    const roleNameAllowed = allowedRoleNames.length === 0 || allowedRoleNames.includes(roleName);

    // Logged in but role not allowed
    if ((allowedRoles.length && !hasRole(allowedRoles)) || !roleNameAllowed || contextMismatch || (disallowManager && isManager)) {
        return (
            <div className="flex flex-col items-center justify-center h-screen p-4 text-center">
                <h1 className="text-3xl font-semibold mb-4 text-red-600">Access Denied</h1>
                <p className="mb-6">You are not authorized to access this page.</p>
                <button
                    onClick={() => logout()}
                    className="px-4 py-2 bg-[#081A4B] text-white rounded-md hover:bg-[#061533]"
                >
                    Logout & Login Again
                </button>
            </div>
        );
    }

    // Authorized → render children
    return children;
};

export default ProtectedRoute;
